import React, { useCallback, useEffect, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingService } from '../../services/marketing'
import type { MarketingGame, MarketingGameQuestion } from '../../types/marketing'
import { formatDateBr, normalizePuzzleWord } from './gamePuzzle'
import GameSimularModal from './GameSimularModal'

const MODULE_NAME = 'marketing_game'
const PERM_LOADING: React.ReactNode = null
const MAX_QUESTIONS = 12
const BOARD_MAX = 15

interface GameFormState {
  id?: string
  title: string
  description: string
  start_date: string
  end_date: string
  is_active: boolean
  questions: MarketingGameQuestion[]
}

const emptyQuestion = (): MarketingGameQuestion => ({ clue: '', word: '' })

function emptyForm(): GameFormState {
  const today = new Date().toISOString().slice(0, 10)
  return {
    title: '',
    description: '',
    start_date: today,
    end_date: today,
    is_active: true,
    questions: [emptyQuestion()],
  }
}

const GameCadastroTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [games, setGames] = useState<MarketingGame[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<GameFormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [testGame, setTestGame] = useState<MarketingGame | null>(null)

  const loadGames = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await marketingService.listGames()
      setGames(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar jogos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadGames()
  }, [loadGames])

  const openNew = () => {
    setForm(emptyForm())
    setFormError(null)
    setModalOpen(true)
  }

  const openEdit = (game: MarketingGame) => {
    setForm({
      id: game.id,
      title: game.title,
      description: game.description || '',
      start_date: game.start_date.slice(0, 10),
      end_date: game.end_date.slice(0, 10),
      is_active: game.is_active,
      questions:
        game.questions.length > 0
          ? game.questions.map((q) => ({ clue: q.clue, word: q.word }))
          : [emptyQuestion()],
    })
    setFormError(null)
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setFormError(null)
  }

  const updateQuestion = (index: number, field: keyof MarketingGameQuestion, value: string) => {
    setForm((prev) => {
      const questions = prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: field === 'word' ? value.toUpperCase() : value } : q
      )
      return { ...prev, questions }
    })
  }

  const addQuestion = () => {
    setForm((prev) => {
      if (prev.questions.length >= MAX_QUESTIONS) return prev
      return { ...prev, questions: [...prev.questions, emptyQuestion()] }
    })
  }

  const removeQuestion = (index: number) => {
    setForm((prev) => {
      if (prev.questions.length <= 1) return prev
      return { ...prev, questions: prev.questions.filter((_, i) => i !== index) }
    })
  }

  const validateForm = (): string | null => {
    if (!form.title.trim()) return 'Informe o título do jogo.'
    if (!form.start_date || !form.end_date) return 'Informe as datas de início e fim.'
    if (form.end_date < form.start_date) return 'A data fim deve ser maior ou igual à data início.'

    const filled = form.questions.filter(
      (q) => q.clue.trim() && normalizePuzzleWord(q.word).length >= 2
    )
    if (filled.length < 1) return 'Informe pelo menos 1 pergunta com palavra válida.'

    const words = filled.map((q) => normalizePuzzleWord(q.word))
    const unique = new Set(words)
    if (unique.size !== words.length) return 'Há palavras duplicadas. Cada resposta deve ser única.'

    const tooLong = words.find((w) => w.length > BOARD_MAX)
    if (tooLong) return `A palavra "${tooLong}" é maior que o tabuleiro (${BOARD_MAX} letras).`

    return null
  }

  const handleSave = async () => {
    const validationError = validateForm()
    if (validationError) {
      setFormError(validationError)
      return
    }

    const questions = form.questions
      .map((q) => ({
        clue: q.clue.trim(),
        word: normalizePuzzleWord(q.word),
      }))
      .filter((q) => q.clue && q.word.length >= 2)
      .slice(0, MAX_QUESTIONS)

    try {
      setSaving(true)
      setFormError(null)

      if (form.id) {
        const updated = await marketingService.updateGame(form.id, {
          title: form.title.trim(),
          description: form.description.trim() || null,
          start_date: form.start_date,
          end_date: form.end_date,
          is_active: form.is_active,
          questions,
        })
        void logUpdate(
          'marketing_game',
          updated.id,
          `Atualizou jogo de caça-palavras: ${updated.title}`,
          undefined,
          {
            title: updated.title,
            start_date: updated.start_date,
            end_date: updated.end_date,
            questions_count: updated.questions.length,
          }
        )
      } else {
        const created = await marketingService.createGame({
          title: form.title.trim(),
          description: form.description.trim() || null,
          start_date: form.start_date,
          end_date: form.end_date,
          is_active: form.is_active,
          questions,
          created_by: user?.id ?? null,
        })
        void logCreate(
          'marketing_game',
          created.id,
          `Criou jogo de caça-palavras: ${created.title}`,
          {
            title: created.title,
            start_date: created.start_date,
            end_date: created.end_date,
            questions_count: created.questions.length,
          }
        )
      }

      setModalOpen(false)
      await loadGames()
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erro ao salvar jogo')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (game: MarketingGame) => {
    if (!window.confirm(`Excluir o jogo "${game.title}"? As partidas do ranking também serão removidas.`)) {
      return
    }
    try {
      setDeletingId(game.id)
      await marketingService.deleteGame(game.id)
      void logDelete('marketing_game', game.id, `Excluiu jogo de caça-palavras: ${game.title}`, {
        title: game.title,
      })
      await loadGames()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir jogo')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Jogos cadastrados
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Até {MAX_QUESTIONS} perguntas por jogo. Defina o período em que o caça-palavras pode ser jogado.
          </Text>
        </div>
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="insert"
          fallback={null}
          loadingComponent={PERM_LOADING}
        >
          <Button variant="solid" onClick={openNew} className="flex items-center gap-1">
            <PlusIcon className="h-5 w-5" />
            Novo jogo
          </Button>
        </PermissionGuard>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Título
                </th>
                <th className="min-w-[180px] px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Período
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Perguntas
                </th>
                <th className="min-w-[100px] px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="min-w-[120px] px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Vigência
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {games.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nenhum jogo cadastrado ainda.
                  </td>
                </tr>
              ) : (
                games.map((game) => {
                  const today = new Date().toISOString().slice(0, 10)
                  const start = (game.start_date || '').slice(0, 10)
                  const end = (game.end_date || '').slice(0, 10)
                  const inPeriod = !!start && !!end && start <= today && end >= today
                  const vigente = game.is_active && inPeriod

                  return (
                  <tr key={game.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{game.title}</div>
                      {game.description && (
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                          {game.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div className="font-medium">
                        {formatDateBr(game.start_date)} até {formatDateBr(game.end_date)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Início - fim para jogar
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {game.questions.length}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          game.is_active
                            ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {game.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          vigente
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        }`}
                      >
                        {vigente ? 'Disponível agora' : 'Fora do período'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setTestGame(game)}
                          disabled={game.questions.length < 1}
                          className="inline-flex items-center gap-1 rounded-md border border-[#008C9A]/30 bg-[#008C9A]/10 px-2.5 py-1 text-xs font-semibold text-[#016B76] hover:bg-[#008C9A]/20 disabled:cursor-not-allowed disabled:opacity-40 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-200"
                          title="Abrir simulação do caça-palavras"
                        >
                          <PlayIcon className="h-3.5 w-3.5" />
                          Teste
                        </button>
                        <PermissionGuard
                          moduleName={MODULE_NAME}
                          permissionType="update"
                          fallback={null}
                          loadingComponent={PERM_LOADING}
                        >
                          <button
                            type="button"
                            onClick={() => openEdit(game)}
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard
                          moduleName={MODULE_NAME}
                          permissionType="delete"
                          fallback={null}
                          loadingComponent={PERM_LOADING}
                        >
                          <button
                            type="button"
                            onClick={() => void handleDelete(game)}
                            disabled={deletingId === game.id}
                            className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 disabled:opacity-50"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {form.id ? 'Editar jogo' : 'Novo jogo'}
              </Heading>
              <button
                type="button"
                onClick={closeModal}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  {formError}
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Título
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Ex.: Circulando | Desafio possível - Ed. 244"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Descrição
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  placeholder="Texto de apoio / edição"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data início
                  </label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data fim
                  </label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Jogo ativo
                  </label>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Perguntas e palavras ({form.questions.length}/{MAX_QUESTIONS})
                </label>
                <Text size="xs" color="muted" className="mb-3">
                  A palavra resposta será normalizada (sem acentos/espaços) e deve caber no tabuleiro 15×15.
                </Text>
                <div className="space-y-3">
                  {form.questions.map((q, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-900/40"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                          Pergunta {index + 1}
                        </span>
                        {form.questions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeQuestion(index)}
                            className="text-xs text-red-600 hover:underline dark:text-red-400"
                          >
                            Remover
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <textarea
                          value={q.clue}
                          onChange={(e) => updateQuestion(index, 'clue', e.target.value)}
                          rows={2}
                          placeholder="Dica / pergunta"
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        />
                        <Input
                          value={q.word}
                          onChange={(e) => updateQuestion(index, 'word', e.target.value)}
                          placeholder="Palavra resposta (ex.: MARKETING)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addQuestion}
                    disabled={form.questions.length >= MAX_QUESTIONS}
                    className="w-full sm:w-auto"
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Adicionar
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSave()} disabled={saving} className="flex items-center gap-1">
                {saving ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <CheckIcon className="h-5 w-5" />
                )}
                Salvar
              </Button>
            </div>
          </div>
        </div>
      )}

      {testGame && (
        <GameSimularModal game={testGame} onClose={() => setTestGame(null)} />
      )}
    </div>
  )
}

export default GameCadastroTab
